import React from 'react';
import { Edit3, Trash2, Copy, ArrowUp, ArrowDown, Move } from 'lucide-react';

export default function SalonPreview({
  page,
  theme,
  services,
  staff,
  testimonials,
  gallery,
  contactSettings,
  mapSettings,
  selectedBlock,
  onSelectBlock,
  onEditBlock,
  onDeleteBlock,
  onDuplicateBlock,
  onMoveBlock,
  onInlineEdit,
  isEditing = false
}) {
  if (!page) return null;

  const renderBlock = (block) => {
    const isSelected = selectedBlock === block.block_id;
    
    const blockProps = {
      key: block.block_id,
      className: `preview-block ${isSelected ? 'selected' : ''}`,
      onClick: (e) => {
        e.stopPropagation();
        if (isEditing) onSelectBlock(block.block_id);
      },
      'data-testid': `block-${block.block_type}-${block.block_id}`
    };

    const renderControls = () => {
      if (!isEditing) return null;
      return (
        <div className="block-controls">
          <button 
            className="block-control-btn" 
            onClick={(e) => { e.stopPropagation(); onMoveBlock(block.block_id, 'up'); }}
            title="Move up"
          >
            <ArrowUp size={14} />
          </button>
          <button 
            className="block-control-btn" 
            onClick={(e) => { e.stopPropagation(); onMoveBlock(block.block_id, 'down'); }}
            title="Move down"
          >
            <ArrowDown size={14} />
          </button>
          <button 
            className="block-control-btn" 
            onClick={(e) => { e.stopPropagation(); onEditBlock(block); }}
            title="Edit"
          >
            <Edit3 size={14} />
          </button>
          <button 
            className="block-control-btn" 
            onClick={(e) => { e.stopPropagation(); onDuplicateBlock(block); }}
            title="Duplicate"
          >
            <Copy size={14} />
          </button>
          <button 
            className="block-control-btn" 
            onClick={(e) => { e.stopPropagation(); onDeleteBlock(block.block_id); }}
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      );
    };

    switch (block.block_type) {
      case 'header':
        return (
          <div {...blockProps}>
            {renderControls()}
            <header className="salon-header">
              <div 
                className="salon-logo inline-editable"
                contentEditable={isEditing}
                suppressContentEditableWarning
                onBlur={(e) => onInlineEdit(block.block_id, 'salon_name', e.target.textContent)}
              >
                {block.content.salon_name || theme?.salon_name || 'Luxe Salon'}
              </div>
              <nav className="salon-nav">
                {(block.content.nav_items || ['Home', 'Services', 'Staff', 'About', 'Blog']).map((item, i) => (
                  <span key={i} className={`salon-nav-item ${i === 0 ? 'active' : ''}`}>{item}</span>
                ))}
                <button style={{ 
                  background: 'none', 
                  border: '1px solid #ccc', 
                  padding: '6px 10px', 
                  borderRadius: '4px', 
                  cursor: 'pointer' 
                }}>
                  <ArrowDown size={14} />
                </button>
              </nav>
            </header>
          </div>
        );

      case 'hero':
        return (
          <div {...blockProps}>
            {renderControls()}
            <div 
              className="salon-hero"
              style={{ 
                backgroundImage: `url(${block.content.background_image || 'https://images.unsplash.com/photo-1759134198561-e2041049419c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85'})` 
              }}
            >
              <div className="hero-content">
                <h1 
                  className="hero-title inline-editable"
                  contentEditable={isEditing}
                  suppressContentEditableWarning
                  onBlur={(e) => onInlineEdit(block.block_id, 'heading', e.target.textContent)}
                >
                  {block.content.heading || 'Luxe Salon'}
                </h1>
                <p 
                  className="hero-subtitle inline-editable"
                  contentEditable={isEditing}
                  suppressContentEditableWarning
                  onBlur={(e) => onInlineEdit(block.block_id, 'subheading', e.target.textContent)}
                >
                  {block.content.subheading || 'Style & Beauty Redefined'}
                </p>
                <button 
                  className="hero-cta"
                  style={{ background: theme?.primary_color || '#8B7355' }}
                >
                  {block.content.cta_text || 'Book Appointment'}
                </button>
              </div>
              {/* Selection handles */}
              {isSelected && (
                <>
                  <div className="selection-handle top" style={{ position: 'absolute', bottom: '-5px', left: '50%', transform: 'translateX(-50%)' }} />
                </>
              )}
            </div>
          </div>
        );

      case 'services':
        return (
          <div {...blockProps}>
            {renderControls()}
            <section className="salon-section">
              <h2 
                className="section-title inline-editable"
                contentEditable={isEditing}
                suppressContentEditableWarning
                onBlur={(e) => onInlineEdit(block.block_id, 'section_title', e.target.textContent)}
              >
                {block.content.section_title || 'Our Services'}
              </h2>
              <div className="services-grid">
                {services.filter(s => s.active !== false).map(service => (
                  <div key={service.id} className="service-card">
                    <img 
                      src={service.image_url || 'https://images.unsplash.com/photo-1582095133179-bfd08e2fc6b3?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85'} 
                      alt={service.service_name}
                      className="service-image"
                    />
                    <div className="service-content">
                      <div className="service-header">
                        <h3 className="service-name">{service.service_name}</h3>
                        <span className="service-price">${service.price}</span>
                      </div>
                      <p className="service-description">{service.description}</p>
                      {isEditing && (
                        <button 
                          className="edit-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditBlock({ ...block, editService: service });
                          }}
                          style={{ background: theme?.primary_color || '#8B7355' }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        );

      case 'staff':
        return (
          <div {...blockProps}>
            {renderControls()}
            <section className="salon-section alt">
              <h2 
                className="section-title inline-editable"
                contentEditable={isEditing}
                suppressContentEditableWarning
                onBlur={(e) => onInlineEdit(block.block_id, 'section_title', e.target.textContent)}
              >
                {block.content.section_title || 'Meet Our Stylists'}
              </h2>
              <div className="staff-grid">
                {staff.filter(s => s.active !== false).map(member => (
                  <div key={member.id} className="staff-card">
                    <img 
                      src={member.photo_url || 'https://images.unsplash.com/photo-1602501025442-a4899b5e0b71?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85'} 
                      alt={member.name}
                      className="staff-photo"
                    />
                    <h3 className="staff-name">{member.name}</h3>
                    <p className="staff-title">{member.title}</p>
                    {isEditing && (
                      <button 
                        className="edit-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditBlock({ ...block, editStaff: member });
                        }}
                        style={{ background: theme?.primary_color || '#8B7355' }}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>
        );

      case 'testimonials':
        return (
          <div {...blockProps}>
            {renderControls()}
            <section className="salon-section">
              <div className="testimonial-section">
                <div className="staff-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                  {staff.filter(s => s.active !== false).slice(0, 3).map(member => (
                    <div key={member.id} className="staff-card" style={{ padding: '16px' }}>
                      <img 
                        src={member.photo_url || 'https://images.unsplash.com/photo-1602501025442-a4899b5e0b71'} 
                        alt={member.name}
                        className="staff-photo"
                        style={{ width: '100px', height: '100px' }}
                      />
                      <h3 className="staff-name" style={{ fontSize: '16px' }}>{member.name}</h3>
                      <p className="staff-title" style={{ fontSize: '12px', marginBottom: '8px' }}>{member.title}</p>
                      {isEditing && (
                        <button 
                          className="edit-btn" 
                          style={{ padding: '6px 16px', fontSize: '11px', background: theme?.primary_color || '#8B7355' }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                
                {testimonials.length > 0 && (
                  <div className="testimonial-card">
                    <span className="quote-mark">"</span>
                    <p 
                      className="testimonial-quote inline-editable"
                      contentEditable={isEditing}
                      suppressContentEditableWarning
                    >
                      {testimonials[0]?.quote || 'Amazing experience! My hair has never looked better!'}
                    </p>
                    <p className="testimonial-author">- {testimonials[0]?.client_name || 'Jessica M.'}</p>
                    <span className="quote-mark end">"</span>
                  </div>
                )}
              </div>
            </section>
          </div>
        );

      case 'gallery':
        return (
          <div {...blockProps}>
            {renderControls()}
            <section className="salon-section alt">
              <h2 className="section-title">{block.content.section_title || 'Gallery'}</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                {gallery.filter(g => g.active !== false).map(img => (
                  <div key={img.id} style={{ position: 'relative' }}>
                    <img 
                      src={img.image_url} 
                      alt={img.alt_text || 'Gallery image'}
                      style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '8px' }}
                    />
                  </div>
                ))}
              </div>
            </section>
          </div>
        );

      case 'map':
        return (
          <div {...blockProps}>
            {renderControls()}
            <section className="salon-section">
              <h2 className="section-title">{block.content.title || mapSettings?.location_title || 'Visit Us'}</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
                <div style={{ 
                  background: '#f5f5f5', 
                  borderRadius: '8px', 
                  height: '300px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#666'
                }}>
                  Map Preview
                </div>
                <div>
                  <h3 style={{ fontFamily: 'Playfair Display, serif', marginBottom: '16px' }}>
                    {theme?.salon_name || 'Luxe Salon'}
                  </h3>
                  <p style={{ color: '#666', marginBottom: '8px' }}>{mapSettings?.business_address}</p>
                  <p style={{ color: '#666', marginBottom: '8px' }}>{mapSettings?.phone}</p>
                  <p style={{ color: '#666' }}>{mapSettings?.hours}</p>
                </div>
              </div>
            </section>
          </div>
        );

      case 'contact_form':
        return (
          <div {...blockProps}>
            {renderControls()}
            <section className="salon-section alt">
              <h2 className="section-title">{contactSettings?.form_title || 'Contact Us'}</h2>
              <div style={{ maxWidth: '600px' }}>
                <div className="form-field">
                  <input type="text" placeholder="Your Name" disabled={!isEditing} />
                </div>
                <div className="form-field">
                  <input type="email" placeholder="Email Address" disabled={!isEditing} />
                </div>
                <div className="form-field">
                  <input type="tel" placeholder="Phone Number" disabled={!isEditing} />
                </div>
                <div className="form-field">
                  <textarea placeholder="Your Message" disabled={!isEditing} />
                </div>
                <button 
                  className="form-submit-btn"
                  style={{ background: theme?.accent_color || '#5A9A5A' }}
                >
                  {contactSettings?.submit_label || 'Send'}
                </button>
              </div>
            </section>
          </div>
        );

      case 'text':
        return (
          <div {...blockProps}>
            {renderControls()}
            <section className="salon-section">
              <p 
                className="inline-editable"
                contentEditable={isEditing}
                suppressContentEditableWarning
                onBlur={(e) => onInlineEdit(block.block_id, 'text', e.target.textContent)}
                style={{ fontSize: '16px', lineHeight: '1.8', color: '#444' }}
              >
                {block.content.text || 'Enter your text here...'}
              </p>
            </section>
          </div>
        );

      case 'image':
        return (
          <div {...blockProps}>
            {renderControls()}
            <section className="salon-section">
              {block.content.url ? (
                <img 
                  src={block.content.url} 
                  alt={block.content.alt || ''}
                  style={{ maxWidth: '100%', borderRadius: '8px' }}
                />
              ) : (
                <div style={{ 
                  background: '#f5f5f5', 
                  height: '300px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  borderRadius: '8px',
                  color: '#999'
                }}>
                  Click to add image
                </div>
              )}
            </section>
          </div>
        );

      case 'button':
        return (
          <div {...blockProps}>
            {renderControls()}
            <section className="salon-section" style={{ textAlign: 'center' }}>
              <button 
                style={{
                  padding: '14px 32px',
                  background: theme?.primary_color || '#8B7355',
                  color: 'white',
                  border: 'none',
                  borderRadius: theme?.button_style === 'pill' ? '50px' : '4px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                {block.content.text || 'Click Here'}
              </button>
            </section>
          </div>
        );

      default:
        return null;
    }
  };

  // Sort blocks by order_index
  const sortedBlocks = [...(page.blocks || [])].sort((a, b) => a.order_index - b.order_index);

  return (
    <div className="salon-preview" data-testid="salon-preview" onClick={() => onSelectBlock && onSelectBlock(null)}>
      {sortedBlocks.map(block => block.visible !== false && renderBlock(block))}
    </div>
  );
}
